// src/components/ContactUs/ContactForm/ContactForm.js
import React from 'react';
import { Formik, Form, useField } from 'formik';
import * as Yup from 'yup';
import emailjs from 'emailjs-com';
import './contact-us.scss'; // use your existing stylesheet; no changes needed

// === EmailJS config (your real values) ===
const EMAILJS_SERVICE_ID = 'service_wz7r81u';
const EMAILJS_TEMPLATE_ID = 'template_6m8762v';
const EMAILJS_PUBLIC_KEY  = 'S_kat3ui3iF7PRQV4';

// Initialize EmailJS once (safe to call multiple times)
emailjs.init(EMAILJS_PUBLIC_KEY);

/** Small error text helper (styled by .error in your SCSS block) */
const FieldError = ({ id, children }) => (
  <div id={id} className="error" role="alert">
    {children}
  </div>
);

/** Inputs use only classes your SCSS already knows */
const TextInput = ({ label, required, ...props }) => {
  const [field, meta] = useField(props);
  const showError = meta.touched && meta.error;
  return (
    <div className="input-wrap">
      <label htmlFor={props.id || props.name}>
        {label} {required && <span className="required">*</span>}
      </label>
      <input
        {...field}
        {...props}
        aria-invalid={!!showError}
        aria-describedby={showError ? `${props.name}-error` : undefined}
        className={showError ? 'has-error' : undefined}
      />
      {showError && <FieldError id={`${props.name}-error`}>{meta.error}</FieldError>}
    </div>
  );
};

const TextArea = ({ label, required, ...props }) => {
  const [field, meta] = useField(props);
  const showError = meta.touched && meta.error;
  return (
    <div className="input-wrap">
      <label htmlFor={props.id || props.name}>
        {label} {required && <span className="required">*</span>}
      </label>
      <textarea
        {...field}
        {...props}
        aria-invalid={!!showError}
        aria-describedby={showError ? `${props.name}-error` : undefined}
        className={showError ? 'has-error' : undefined}
      />
      {showError && <FieldError id={`${props.name}-error`}>{meta.error}</FieldError>}
    </div>
  );
};

const SelectInput = ({ label, required, children, ...props }) => {
  const [field, meta] = useField(props);
  const showError = meta.touched && meta.error;
  return (
    <div className="input-wrap">
      <label htmlFor={props.id || props.name}>
        {label} {required && <span className="required">*</span>}
      </label>
      <select
        {...field}
        {...props}
        aria-invalid={!!showError}
        aria-describedby={showError ? `${props.name}-error` : undefined}
        className={showError ? 'has-error' : undefined}
      >
        {children}
      </select>
      {showError && <FieldError id={`${props.name}-error`}>{meta.error}</FieldError>}
    </div>
  );
};

/** Validation */
const ValidationSchema = Yup.object({
  fullName: Yup.string().trim().required('Full name is required'),
  email: Yup.string().trim().required('Email is required').email('Please enter a valid email address'),
  contact: Yup.string().trim().required('Contact number is required'),
  inquiryType: Yup.string().required('Please select an inquiry type'),
  orderNumber: Yup.string().when('inquiryType', {
    is: (v) => v === 'Order Support' || v === 'Refund Request',
    then: (schema) => schema.required('Order number is required'),
    otherwise: (schema) => schema.notRequired(),
  }),
  subject: Yup.string().trim().required('Subject is required'),
  message: Yup.string().trim().required('Message is required'),
});

const ContactForm = () => {
  return (
    <Formik
      initialValues={{
        fullName: '',
        email: '',
        contact: '',
        inquiryType: '',
        orderNumber: '',
        subject: '',
        message: '',
      }}
      validationSchema={ValidationSchema}
      onSubmit={async (values, { resetForm, setSubmitting }) => {
        try {
          // Send with BOTH common EmailJS variable names and your custom ones.
          // Ensure your EmailJS template uses these placeholders, e.g.:
          // {{from_name}}, {{from_email}}, {{reply_to}}, {{to_email}}, {{subject}}, {{message}}
          // or your custom {{fullName}}, {{email}}, etc.
          const templateParams = {
            // EmailJS-conventional names
            from_name: values.fullName,
            from_email: values.email,
            reply_to: values.email,
            to_email: 'support@teleportapp.sg', // optional if set in template
            subject: values.subject,
            message: values.message,

            // Your custom names (in case your template uses these)
            fullName: values.fullName,
            email: values.email,
            contact: values.contact,
            inquiryType: values.inquiryType,
            orderNumber: values.orderNumber || 'N/A',

            // Snake_case variants (some templates use these)
            contact_number: values.contact,
            inquiry_type: values.inquiryType,
            order_number: values.orderNumber || 'N/A',
          };

          await emailjs.send(
            EMAILJS_SERVICE_ID,
            EMAILJS_TEMPLATE_ID,
            templateParams,
            EMAILJS_PUBLIC_KEY
          );

          alert('Thanks for reaching out! Our Teleport Now crew will get back to you in no time 🚀');
          resetForm();
        } catch (error) {
          console.error('EmailJS Error:', error);
          alert('Sorry, something went wrong sending your message. Please try again or email support@teleportapp.sg directly.');
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {({ values, isSubmitting }) => {
        const needsOrder =
          values.inquiryType === 'Order Support' || values.inquiryType === 'Refund Request';

        return (
          <Form className="contact-form">
            {/* Row 1: Full Name + Email */}
            <div className="row">
              <div className="col-md-6">
                <TextInput name="fullName" id="fullName" type="text" label="Full Name" required />
              </div>
              <div className="col-md-6">
                <TextInput name="email" id="email" type="email" label="Email Address" required />
              </div>
            </div>

            {/* Row 2: Contact + Inquiry Type */}
            <div className="row">
              <div className="col-md-6">
                <TextInput name="contact" id="contact" type="tel" label="Contact Number" required />
              </div>
              <div className="col-md-6">
                <SelectInput
                  name="inquiryType"
                  id="inquiryType"
                  label="Inquiry Type"
                  required
                  defaultValue=""
                >
                  <option value="">-- Select Inquiry Type --</option>
                  <option value="General Support">General Support</option>
                  <option value="Order Support">Order Support</option>
                  <option value="Technical Support">Technical Support</option>
                  <option value="Partnerships & Collaborations">Partnerships & Collaborations</option>
                  <option value="Refund Request">Refund Request</option>
                  <option value="Bugs & Suggestions">Bugs & Suggestions</option>
                  <option value="Account Deletion">Account Deletion</option>
                  <option value="Others">Others</option>
                </SelectInput>
              </div>
            </div>

            {/* Row 3: Order Number (full width, conditional) */}
            <div className="row">
              <div className={`col-md-12 ${needsOrder ? '' : 'hidden'}`}>
                <TextInput
                  name="orderNumber"
                  id="orderNumber"
                  type="text"
                  label="Teleport Now Order Number"
                  required={needsOrder}
                />
              </div>
            </div>

            {/* Row 4: Subject (full width) */}
            <div className="row">
              <div className="col-md-12">
                <TextInput name="subject" id="subject" type="text" label="Subject" required />
              </div>
            </div>

            {/* Row 5: Message (full width) */}
            <div className="row">
              <div className="col-md-12">
                <TextArea name="message" id="message" label="Message" required />
              </div>
            </div>

            {/* Submit – styled by your SCSS */}
            <button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Sending…' : 'Submit'}
            </button>
          </Form>
        );
      }}
    </Formik>
  );
};

export default ContactForm;
